import React, { useState, useEffect } from 'react';
import styles from './CustomerProfile.module.css';
import { fetchCustomerDetails, updateCustomer, deleteCustomer, fetchCustomerUnits, exportCustomerUnitTemplate } from '../services/customerService';
import InstallmentTable from './InstallmentTable';
import Dialog from './Dialog';
import { fetchCustomerRemainingInstallments, fetchCustomerPreviousInstallments } from '../services/installmentService';
import { useCachedPermissions } from '../hooks/useCachedPermissions';
import { useDispatch } from 'react-redux';
import { showErrorNotification } from '../store/slices/notificationSlice';

const CustomerProfile = ({ customerId, onBack, onEdit, onDelete }) => {
    const dispatch = useDispatch();

    // Customer permissions
    const { hasUpdateCustomer, hasDeleteCustomer, permissionsLoading } = useCachedPermissions();

    const [customer, setCustomer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [editedCustomer, setEditedCustomer] = useState(null);
    const [showInstallmentTable, setShowInstallmentTable] = useState(false);
    const [installmentTableType, setInstallmentTableType] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [updateError, setUpdateError] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [installmentData, setInstallmentData] = useState([]);
    const [installmentLoading, setInstallmentLoading] = useState(false);
    const [installmentError, setInstallmentError] = useState('');
    const [installmentPage, setInstallmentPage] = useState(1);
    const [installmentTotalPages, setInstallmentTotalPages] = useState(1);

    // Template dialog states
    const [showTemplateDialog, setShowTemplateDialog] = useState(false);
    const [customerUnits, setCustomerUnits] = useState([]);
    const [selectedUnitId, setSelectedUnitId] = useState('');
    const [unitsLoading, setUnitsLoading] = useState(false);
    const [unitsError, setUnitsError] = useState('');
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (!customerId) return;
        fetchCustomerDetailsData();
    }, [customerId]);

    useEffect(() => {
        if (!showInstallmentTable || !customerId || !installmentTableType) return;

        const fetchInstallments = async () => {
            setInstallmentLoading(true);
            setInstallmentError('');
            try {
                let response;
                if (installmentTableType === 'remaining') {
                    response = await fetchCustomerRemainingInstallments(customerId, installmentPage, 16);
                } else if (installmentTableType === 'previous') {
                    response = await fetchCustomerPreviousInstallments(customerId, installmentPage, 16);
                }
                setInstallmentData(response?.data?.data || []);
                setInstallmentTotalPages(response?.data?.last_page || 1);
            } catch (err) {
                setInstallmentError(err.message || 'Failed to fetch installments');
                setInstallmentData([]);
            } finally {
                setInstallmentLoading(false);
            }
        };

        fetchInstallments();
    }, [showInstallmentTable, installmentTableType, customerId, installmentPage]);

    const fetchCustomerDetailsData = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await fetchCustomerDetails(customerId);
            console.log('Customer details response:', response);
            if (response.data) {
                setCustomer(response.data);
                setEditedCustomer(response.data);
            } else {
                setCustomer(null);
                setError('Customer not found');
            }
        } catch (err) {
            console.error('Error fetching customer details:', err);
            setError(err.message || 'Failed to fetch customer details');
            setCustomer(null);
        } finally {
            setLoading(false);
        }
    };

    // Form validation
    const isFormValid = () => {
        return editedCustomer && editedCustomer.name && editedCustomer.name.trim() !== '' &&
            editedCustomer.address && editedCustomer.address.trim() !== '';
    };

    const handleEdit = async () => {
        if (!hasUpdateCustomer) {
            dispatch(showErrorNotification('You do not have permission to update customers'));
            return;
        }

        if (isEditing) {
            // Save changes
            await handleSaveChanges();
        } else {
            // Start editing
            setIsEditing(true);
            setUpdateError('');
        }
    };

    const handleSaveChanges = async () => {
        if (!isFormValid()) return;

        setIsSubmitting(true);
        setUpdateError('');

        try {
            const formData = new FormData();
            formData.append('name', editedCustomer.name);
            formData.append('email', editedCustomer.email || '');
            formData.append('phone_code', editedCustomer.phone_code || '+966');
            formData.append('phone', editedCustomer.phone || '');
            // formData.append('gender', editedCustomer.gender || '');
            formData.append('address', editedCustomer.address);
            formData.append('job', editedCustomer.job || '');
            formData.append('swag_id', editedCustomer.swag_id || '');
            formData.append('nid', editedCustomer.nid || '');

            // Handle profile picture if it's a file
            if (editedCustomer.profile_picture && editedCustomer.profile_picture.startsWith('data:')) {
                // Convert base64 to file
                const response = await fetch(editedCustomer.profile_picture);
                const blob = await response.blob();
                const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
                formData.append('profile', file);
            }

            const result = await updateCustomer(customerId, formData);
            console.log('Update result:', result);

            // Update the customer data with the response
            if (result.data) {
                setCustomer(result.data);
                setEditedCustomer(result.data);
            }

            setIsEditing(false);
            setUpdateError('');

            // Call the parent onEdit callback if provided
            if (onEdit) {
                onEdit(result.data);
            }

            // Go back to customers list after successful update
            if (onBack) {
                onBack();
            }
        } catch (err) {
            console.error('Error updating customer:', err);
            setUpdateError(err.message || 'Failed to update customer');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        setEditedCustomer(customer);
        setIsEditing(false);
        setUpdateError('');
    };

    const handleDelete = async () => {
        if (!hasDeleteCustomer) {
            dispatch(showErrorNotification('You do not have permission to delete customers'));
            return;
        }

        if (!window.confirm('Are you sure you want to delete this customer? This action cannot be undone.')) {
            return;
        }

        setIsDeleting(true);
        setDeleteError('');

        try {
            await deleteCustomer(customerId);
            console.log('Customer deleted successfully');

            // Call the parent onDelete callback if provided
            if (onDelete) {
                onDelete(customerId);
            }

            // Go back to customers list after successful deletion
            if (onBack) {
                onBack();
            }
        } catch (err) {
            console.error('Error deleting customer:', err);
            setDeleteError(err.message || 'Failed to delete customer');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleFieldChange = (field, value) => {
        setEditedCustomer(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleImageEdit = () => {
        // Create a file input element
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.accept = 'image/*';

        fileInput.onchange = (e) => {
            const file = e.target.files[0];
            if (file) {
                const reader = new FileReader();
                reader.onload = (event) => {
                    setEditedCustomer(prev => ({
                        ...prev,
                        profile_picture: event.target.result
                    }));
                };
                reader.readAsDataURL(file);
            }
        };

        fileInput.click();
    };

    const handleShowInstallmentTable = (type) => {
        setInstallmentPage(1);
        setInstallmentTableType(type);
        setShowInstallmentTable(true);
    };

    const handleCloseInstallmentTable = () => {
        setShowInstallmentTable(false);
        setInstallmentTableType('');
    };

    const getInstallmentData = () => installmentData;

    const getInstallmentTitle = () => {
        if (installmentTableType === 'previous') {
            return 'Previous Installments';
        } else if (installmentTableType === 'remaining') {
            return 'Remaining Installments';
        }
        return '';
    };

    // Template dialog functions
    const handleShowTemplateDialog = async () => {
        setShowTemplateDialog(true);
        setUnitsLoading(true);
        setUnitsError('');

        try {
            const response = await fetchCustomerUnits(customerId);
            if (response.data && response.data.length > 0) {
                setCustomerUnits(response.data);
                setSelectedUnitId(response.data[0].unit_id); // Set first unit as default
            } else {
                setCustomerUnits([]);
                setUnitsError('No units found for this customer');
            }
        } catch (err) {
            console.error('Error fetching customer units:', err);
            setUnitsError(err.message || 'Failed to fetch customer units');
            setCustomerUnits([]);
        } finally {
            setUnitsLoading(false);
        }
    };

    const handleCloseTemplateDialog = () => {
        setShowTemplateDialog(false);
        setCustomerUnits([]);
        setSelectedUnitId('');
        setUnitsError('');
    };

    const handleUnitSelection = (unitId) => {
        setSelectedUnitId(unitId);
    };

    const handleExportTemplate = async () => {
        if (!selectedUnitId) {
            setUnitsError('Please select a unit');
            return;
        }

        setIsExporting(true);
        try {
            await exportCustomerUnitTemplate(customerId, selectedUnitId);
            handleCloseTemplateDialog();
        } catch (err) {
            console.error('Error exporting template:', err);
            setUnitsError(err.message || 'Failed to export template');
        } finally {
            setIsExporting(false);
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return 'N/A';
        return new Intl.NumberFormat('en-EG', {
            style: 'currency',
            currency: 'EGP',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dateString; // API already provides formatted date
    };

    if (loading) {
        return (
            <div className={styles.profileContainer}>
                <div className={styles.contentContainer}>
                    <div className={styles.loadingMessage}>
                        <p>Loading customer details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !customer) {
        return (
            <div className={styles.profileContainer}>
                <div className={styles.headerContainer}>
                    <div className={styles.headerLeft}>
                        <button className={styles.backButton} onClick={onBack}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5" />
                                <path d="M12 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                        <h2 className={styles.headerTitle}>Customer Not Found</h2>
                    </div>
                </div>
                <div className={styles.contentContainer}>
                    <div className={styles.errorMessage}>
                        <p>{error || 'This customer is no longer available.'}</p>
                        <button onClick={fetchCustomerDetailsData} className={styles.retryButton}>
                            Retry
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const customerData = isEditing ? editedCustomer : customer;

    return (
        <div className={styles.profileContainer}>
            {/* Header - Only show when not in table view */}
            {!showInstallmentTable && (
                <div className={styles.headerContainer}>
                    <div className={styles.headerLeft}>
                        <button className={styles.backButton} onClick={onBack}>
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5" />
                                <path d="M12 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                        <h2 className={styles.headerTitle}>Profile</h2>
                    </div>
                    <div className={styles.headerActions}>
                        {hasUpdateCustomer && (
                            <button
                                className={`${styles.editButton} ${(isEditing && !isFormValid()) || isSubmitting ? styles.disabled : ''}`}
                                onClick={handleEdit}
                                disabled={(isEditing && !isFormValid()) || isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <svg className={styles.spinner} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 12a9 9 0 11-6.219-8.56" />
                                        </svg>
                                        Saving...
                                    </>
                                ) : isEditing ? (
                                    <>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M20 6L9 17l-5-5" />
                                        </svg>
                                        Save
                                    </>
                                ) : (
                                    <>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                        </svg>
                                        Edit
                                    </>
                                )}
                            </button>
                        )}
                        {isEditing && (
                            <button className={styles.cancelButton} onClick={handleCancel}>
                                Cancel
                            </button>
                        )}
                        {customer.has_unit && (<button
                            className={styles.templateButton}
                            onClick={handleShowTemplateDialog}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                <polyline points="14,2 14,8 20,8" />
                                <line x1="16" y1="13" x2="8" y2="13" />
                                <line x1="16" y1="17" x2="8" y2="17" />
                                <polyline points="10,9 9,9 8,9" />
                            </svg>
                            Template
                        </button>)}
                        {hasDeleteCustomer && (
                            <button
                                className={`${styles.deleteButton} ${isDeleting ? styles.disabled : ''}`}
                                onClick={handleDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <svg className={styles.spinner} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 12a9 9 0 11-6.219-8.56" />
                                        </svg>
                                        Deleting...
                                    </>
                                ) : (
                                    <>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="3,6 5,6 21,6" />
                                            <path d="m19,6v14a2,2 0 0,1 -2,2H7a2,2 0 0,1 -2,-2V6m3,0V4a2,2 0 0,1 2,-2h4a2,2 0 0,1 2,2v2" />
                                        </svg>
                                        Delete
                                    </>
                                )}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Update Error Message */}
            {updateError && (
                <div className={styles.errorContainer}>
                    <span className={styles.errorText}>{updateError}</span>
                </div>
            )}

            {/* Delete Error Message */}
            {deleteError && (
                <div className={styles.errorContainer}>
                    <span className={styles.errorText}>{deleteError}</span>
                </div>
            )}

            {/* Content */}
            {!showInstallmentTable ? (
                <div className={styles.contentContainer}>
                    {/* Left Side - Customer Info */}
                    <div className={styles.customerInfoSection}>
                        <div className={styles.customerImage}>
                            <img
                                src={customerData.profile_picture || 'https://static.vecteezy.com/system/resources/previews/005/544/718/original/profile-icon-design-free-vector.jpg'}
                                alt={customerData.name}
                                onError={(e) => {
                                    e.target.src = 'https://static.vecteezy.com/system/resources/previews/005/544/718/original/profile-icon-design-free-vector.jpg';
                                }}
                            />
                            {isEditing && (
                                <div className={styles.imageEditOverlay}>
                                    <button
                                        className={styles.imageEditButton}
                                        onClick={handleImageEdit}
                                        type="button"
                                    >
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                            <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>

                        <div className={styles.customerDetails}>
                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>Name</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={customerData.name}
                                        onChange={(e) => handleFieldChange('name', e.target.value)}
                                        className={styles.fieldInput}
                                    />
                                ) : (
                                    <span className={styles.fieldValue}>{customerData.name}</span>
                                )}
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>Address</label>
                                {isEditing ? (
                                    <textarea
                                        value={customerData.address}
                                        onChange={(e) => handleFieldChange('address', e.target.value)}
                                        className={styles.fieldTextarea}
                                        rows="3"
                                    />
                                ) : (
                                    <span className={styles.fieldValue}>{customerData.address}</span>
                                )}
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>Email</label>
                                {isEditing ? (
                                    <input
                                        type="email"
                                        value={customerData.email || ''}
                                        onChange={(e) => handleFieldChange('email', e.target.value)}
                                        className={styles.fieldInput}
                                    />
                                ) : (
                                    <span className={styles.fieldValue}>{customerData.email || 'Not provided'}</span>
                                )}
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>Job</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={customerData.job || ''}
                                        onChange={(e) => handleFieldChange('job', e.target.value)}
                                        className={styles.fieldInput}
                                        placeholder="Job title"
                                    />
                                ) : (
                                    <span className={styles.fieldValue}>{customerData.job || 'Not provided'}</span>
                                )}
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>Phone</label>
                                {isEditing ? (
                                    <div className={styles.phoneInputGroup}>
                                        <input
                                            type="text"
                                            value={customerData.phone_code || '+966'}
                                            onChange={(e) => handleFieldChange('phone_code', e.target.value)}
                                            className={styles.phoneCodeInput}
                                            placeholder="+966"
                                        />
                                        <input
                                            type="tel"
                                            value={customerData.phone || ''}
                                            onChange={(e) => handleFieldChange('phone', e.target.value)}
                                            className={styles.phoneNumberInput}
                                            placeholder="Phone number"
                                        />
                                    </div>
                                ) : (
                                    <span className={styles.fieldValue}>
                                        {customerData.phone_code && customerData.phone
                                            ? `${customerData.phone_code}${customerData.phone}`
                                            : 'Not provided'}
                                    </span>
                                )}
                            </div>

                            {/* Secondary Phones */}
                            {customerData.secondary_contacts && customerData.secondary_contacts.length > 0 && (
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>Secondary Phones</label>
                                    <div className={styles.secondaryPhonesContainer}>
                                        {customerData.secondary_contacts.map((contact, index) => (
                                            <div key={index} className={styles.secondaryPhone}>
                                                <span className={styles.phoneNumber}>
                                                    {contact.phone_code}{contact.phone}
                                                </span>
                                                <span className={styles.phoneIndex}>Phone {index + 2}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>National ID</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={customerData.nid || ''}
                                        onChange={(e) => handleFieldChange('nid', e.target.value)}
                                        className={styles.fieldInput}
                                        placeholder="Enter National ID"
                                    />
                                ) : (
                                    <span className={styles.fieldValue}>{customerData.nid || 'Not provided'}</span>
                                )}
                            </div>

                            {customerData.nid_attachment && (
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>ID Attachment</label>
                                    <div className={styles.attachmentContainer}>
                                        <a
                                            href={customerData.nid_attachment}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className={styles.attachmentLink}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                                <polyline points="14,2 14,8 20,8"></polyline>
                                                <line x1="16" y1="13" x2="8" y2="13"></line>
                                                <line x1="16" y1="17" x2="8" y2="17"></line>
                                                <polyline points="10,9 9,9 8,9"></polyline>
                                            </svg>
                                            View ID Document
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>Gender</label>
                                <span className={styles.fieldValue}>
                                    {customerData.gender === 'MALE' ? 'Male' :
                                        customerData.gender === 'FEMALE' ? 'Female' :
                                            'Not specified'}
                                </span>
                            </div> */}

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>SWAG ID</label>
                                {isEditing ? (
                                    <input
                                        type="text"
                                        value={customerData.swag_id || ''}
                                        onChange={(e) => handleFieldChange('swag_id', e.target.value)}
                                        className={styles.fieldInput}
                                        placeholder="Enter SWAG ID"
                                    />
                                ) : (
                                    <span className={styles.fieldValue}>{customerData.swag_id || 'Not provided'}</span>
                                )}
                            </div>

                            {/* Contract Date - Only show if user has unit and contract_date is not null */}
                            {customerData.has_unit && customerData.contract_date && (
                                <div className={styles.fieldGroup}>
                                    <label className={styles.fieldLabel}>Contract Date</label>
                                    <span className={styles.fieldValue}>{formatDate(customerData.contract_date)}</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Only show installment section if customer has_unit is true */}
                    {customerData.has_unit && (
                        <div className={styles.installmentSection}>
                            <h3 className={styles.installmentTitle}>Installment Information</h3>

                            <div className={styles.installmentTable}>
                                <table className={styles.installmentInfoTable}>
                                    <tbody>
                                        {customerData.last_installment_amount && (
                                            <>
                                                <tr>
                                                    <td className={styles.tableLabel}>Last Installment Amount</td>
                                                    <td className={styles.tableValue}>
                                                        {formatCurrency(customerData.last_installment_amount)}
                                                        {customerData.last_installment_category && (
                                                            <span className={`${styles.categoryBadge} ${styles[customerData.last_installment_category.toLowerCase()]}`}>
                                                                {customerData.last_installment_category}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className={styles.tableLabel}>Last Installment Date</td>
                                                    <td className={styles.tableValue}>
                                                        {formatDate(customerData.last_installment_date)}
                                                    </td>
                                                </tr>
                                            </>
                                        )}

                                        {customerData.next_installment_amount && (
                                            <>
                                                <tr>
                                                    <td className={styles.tableLabel}>Next Installment Amount</td>
                                                    <td className={styles.tableValue}>
                                                        {formatCurrency(customerData.next_installment_amount)}
                                                        {customerData.next_installment_category && (
                                                            <span className={`${styles.categoryBadge} ${styles[customerData.next_installment_category.toLowerCase()]}`}>
                                                                {customerData.next_installment_category}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td className={styles.tableLabel}>Next Installment Date</td>
                                                    <td className={styles.tableValue}>
                                                        {formatDate(customerData.next_installment_date)}
                                                    </td>
                                                </tr>
                                            </>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            <div className={styles.installmentButtons}>
                                <button
                                    className={`${styles.installmentButton} ${installmentTableType === 'previous' ? styles.active : ''}`}
                                    onClick={() => handleShowInstallmentTable('previous')}
                                >
                                    Previous Installments
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M9 18l6-6-6-6" />
                                    </svg>
                                </button>
                                 
                                    <button
                                        className={`${styles.installmentButton} ${installmentTableType === 'remaining' ? styles.active : ''}`}
                                        onClick={() => handleShowInstallmentTable('remaining')}
                                    >
                                        Remaining Installments
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <path d="M9 18l6-6-6-6" />
                                        </svg>
                                    </button>
                                
                            </div>
                        </div>
                    )}
                </div>
            ) : (
                <>
                    {installmentLoading && <div>Loading installments...</div>}
                    {installmentError && <div style={{ color: 'red' }}>{installmentError}</div>}
                    <InstallmentTable
                        installments={getInstallmentData()}
                        title={getInstallmentTitle()}
                        onClose={handleCloseInstallmentTable}
                        loading={installmentLoading}
                        currentPage={installmentPage}
                        totalPages={installmentTotalPages}
                        onPageChange={setInstallmentPage}
                    />
                </>
            )}

            {/* Template Dialog */}
            <Dialog
                open={showTemplateDialog}
                onClose={handleCloseTemplateDialog}
                title="Export Unit Template"
                actions={
                    <div className={styles.dialogActions}>
                        <button
                            className={styles.cancelButton}
                            onClick={handleCloseTemplateDialog}
                        >
                            Cancel
                        </button>
                        <button
                            className={`${styles.exportButton} ${isExporting ? styles.disabled : ''}`}
                            onClick={handleExportTemplate}
                            disabled={isExporting || !selectedUnitId}
                        >
                            {isExporting ? (
                                <>
                                    <svg className={styles.spinner} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M21 12a9 9 0 11-6.219-8.56" />
                                    </svg>
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                        <polyline points="7,10 12,15 17,10" />
                                        <line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                    Export
                                </>
                            )}
                        </button>
                    </div>
                }
            >
                <div className={styles.templateDialogContent}>
                    {unitsLoading ? (
                        <div className={styles.loadingState}>
                            <svg className={styles.spinner} width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M21 12a9 9 0 11-6.219-8.56" />
                            </svg>
                            <span>Loading units...</span>
                        </div>
                    ) : unitsError ? (
                        <div className={styles.errorState}>
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="10" />
                                <line x1="15" y1="9" x2="9" y2="15" />
                                <line x1="9" y1="9" x2="15" y2="15" />
                            </svg>
                            <span>{unitsError}</span>
                        </div>
                    ) : customerUnits.length === 0 ? (
                        <div className={styles.emptyState}>
                            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2H5a2 2 0 0 0-2-2z" />
                                <path d="M8 21v-4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v4" />
                            </svg>
                            <span>No units available for this customer</span>
                        </div>
                    ) : (
                        <div className={styles.unitsContainer}>
                            <p className={styles.dialogDescription}>
                                Select a unit to export the template:
                            </p>
                            <div className={styles.unitsList}>
                                {customerUnits.map((unit) => (
                                    <label key={unit.unit_id} className={styles.unitOption}>
                                        <input
                                            type="radio"
                                            name="selectedUnit"
                                            value={unit.unit_id}
                                            checked={selectedUnitId === unit.unit_id}
                                            onChange={() => handleUnitSelection(unit.unit_id)}
                                            className={styles.unitRadio}
                                        />
                                        <div className={styles.unitLabel}>
                                            <span className={styles.unitLabelText}>{unit.unit_label}</span>
                                            {/* <span className={styles.unitId}>ID: {unit.unit_id}</span> */}
                                        </div>
                                    </label>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </Dialog>
        </div>
    );
};

export default CustomerProfile; 